import { parseContractEndDate } from '@order-management/dates';
import { formatSalesOrderNumber } from '@order-management/format';
import {
  AvailabilityStatus,
  DeviceAssignmentsStatus,
  DeviceAssignmentsTableRow,
  SerialNumber,
} from '@order-management/types';
import { useCallback, useMemo } from 'react';
import { Element, Icon } from 'react-bulma-components';
import { PiWarningFill } from 'react-icons/pi';
import Conditional from '../../shared/conditional/conditional';
import { formatDateToLocaleString } from '../../shared/utils';
import Link from '../../ui/link/link';
import InfiniteTable, {
  InfiniteTableProps,
} from '../../ui/table/infinite-table/infinite-table';
import { Column } from '../../ui/table/types';

const NO_CURRENT_ASSIGNMENTS_MESSAGE = 'There are no assignments at the moment';
const NO_ORIGINAL_CONFIGURATION_MESSAGE =
  'There is no original configuration available for this device';

export type DeviceDetailsAssignmentsTableProps = Omit<
  InfiniteTableProps<DeviceAssignmentsTableRow>,
  'empty' | 'columns'
> & {
  viewMode: DeviceAssignmentsStatus;
  deviceSerialNumber?: SerialNumber;
};

export function DeviceDetailsAssignmentsTable({
  viewMode,
  dataSource,
  onChange,
  config,
  fetchMore,
  hasMore,
  loadingInitialData,
  deviceSerialNumber,
}: DeviceDetailsAssignmentsTableProps) {
  const emptyAssignmentsContentText = useMemo(() => {
    switch (viewMode) {
      case DeviceAssignmentsStatus.CURRENT: {
        return NO_CURRENT_ASSIGNMENTS_MESSAGE;
      }
      case DeviceAssignmentsStatus.ORIGINAL: {
        return NO_ORIGINAL_CONFIGURATION_MESSAGE;
      }
    }
  }, [viewMode]);

  const resolveSoftwareTitleNavigationUrl = useCallback(
    (el: DeviceAssignmentsTableRow) => {
      const params = new URLSearchParams({
        availabilityStatus: AvailabilityStatus.ALL,
        includeUnrelated: 'true',
      });
      if (deviceSerialNumber) {
        params.set('assignedDevice', deviceSerialNumber.number);
      }
      return `software/${el.licensingProductGroup.id}?${params.toString()}`;
    },
    [deviceSerialNumber]
  );

  const columns: Column<DeviceAssignmentsTableRow>[] = useMemo(() => {
    switch (viewMode) {
      case DeviceAssignmentsStatus.CURRENT: {
        return [
          {
            key: 'softwareSeat.licensingProductGroup.displayName',
            className: 'is-width-25',
            render: (el) => (
              <Link to={resolveSoftwareTitleNavigationUrl(el)}>
                {el.licensingProductGroup.displayName}
              </Link>
            ),
            title: 'Software title',
            sortable: true,
          },
          {
            key: 'softwareSeat.salesOrderLine.salesOrder.number',
            className: 'is-width-15',
            render: (el) => (
              <Link to={`/sales-orders/${el.salesOrder.id}`}>
                {formatSalesOrderNumber(el.salesOrder.number)}
              </Link>
            ),
            title: 'Sales order',
            sortable: true,
          },
          {
            key: 'softwareSeat.licenseKey.contractEndDate',
            className: 'is-width-15',
            render: (el) => parseContractEndDate(el.contractEndDate),
            title: 'Contract end date',
            sortable: true,
          },
          {
            key: 'status',
            className: 'is-width-10',
            render: (el) => (
              <Conditional
                condition={
                  el.contractEndDate &&
                  new Date(el.contractEndDate) < new Date()
                }
              >
                <Conditional.True>
                  <Icon color="danger-dark">
                    <PiWarningFill />
                  </Icon>{' '}
                  Expired
                </Conditional.True>
                <Conditional.False>Active</Conditional.False>
              </Conditional>
            ),
            title: 'Status',
            sortable: false,
          },
          {
            key: 'createdBy.email',
            className: 'is-width-20',
            render: (el) => el.createdBy ?? '',
            title: 'Last modified by',
            sortable: true,
          },
          {
            key: 'createdAt',
            className: 'is-width-15',
            render: (el) => formatDateToLocaleString(el.createdAt),
            title: 'Last modified date',
            sortable: true,
          },
        ];
      }
      case DeviceAssignmentsStatus.ORIGINAL: {
        return [
          {
            key: 'softwareAssignment.softwareSeat.licensingProductGroup.displayName',
            className: 'is-width-30',
            render: (el) => (
              <Link to={resolveSoftwareTitleNavigationUrl(el)}>
                {el.licensingProductGroup.displayName}
              </Link>
            ),
            title: 'Software title',
            sortable: true,
          },
          {
            key: 'softwareAssignment.softwareSeat.salesOrderLine.salesOrder.number',
            className: 'is-width-15',
            render: (el) => (
              <Link to={`/sales-orders/${el.salesOrder.id}`}>
                {formatSalesOrderNumber(el.salesOrder.number)}
              </Link>
            ),
            title: 'Sales order',
            sortable: true,
          },
          {
            key: 'softwareAssignment.softwareSeat.salesOrderLine.salesOrder.endUser',
            className: 'is-width-25',
            render: (el) => el.salesOrder.endUser ?? '',
            title: 'End user',
            sortable: true,
          },
          {
            key: 'softwareAssignment.createdAt',
            className: 'is-width-15',
            render: (el) => formatDateToLocaleString(el.createdAt),
            title: 'Assignment date',
            sortable: true,
          },
          {
            key: 'softwareAssignment.softwareSeat.salesOrderLine.itemFulfillment.contractEndDate',
            className: 'is-width-15',
            render: (el) => parseContractEndDate(el.contractEndDate),
            title: 'Contract end date',
            sortable: false,
          },
        ];
      }
    }
  }, [resolveSoftwareTitleNavigationUrl, viewMode]);

  return (
    <InfiniteTable
      fetchMore={fetchMore}
      hasMore={hasMore}
      rowKey="id"
      loadingInitialData={loadingInitialData}
      config={config}
      dataSource={dataSource}
      columns={columns}
      onChange={onChange}
      empty={
        <Element
          display="flex"
          justifyContent="center"
          className="my-2 has-text-weight-light"
        >
          <span>{emptyAssignmentsContentText}</span>
        </Element>
      }
    />
  );
}

export default DeviceDetailsAssignmentsTable;
